import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
// form
import { useFormik } from 'formik';
// @mui
import {
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  FormControl,
  FormHelperText,
  Box,
} from '@mui/material';
import { MFA_ERROR_CODES, TIPO_LOGIN } from '@siptel-web/common';
import { LoadingButton } from '@mui/lab';

// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import ReCAPTCHA from 'react-google-recaptcha';
import { GOOGLE_CAPTCHA_KEY } from 'src/config';
import { toast } from 'react-toastify';
import { createMessageError } from 'src/utils/messaggeError';
import { useSearchParams } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import MfaCodeDialog from './MfaCodeDialog';

// ----------------------------------------------------------------------
// should be taken from the common folder
const loginTypes = {
  RUT: { value: TIPO_LOGIN.RUT, label: 'RUT' },
  USERNAME: { value: TIPO_LOGIN.USERNAME, label: 'Username' },
};

// Errores del paso 1 (signin) propios del MFA: se muestra el mensaje del
// backend aunque el statusCode sea >= 500 (EMAIL_SEND_FAILED es 503).
const STEP_ONE_MFA_ERRORS = [
  MFA_ERROR_CODES.EMAIL_NOT_CONFIGURED,
  MFA_ERROR_CODES.EMAIL_SEND_FAILED,
];

const TOO_MANY_REQUESTS_MESSAGE =
  'Demasiadas solicitudes. Intente nuevamente en unos minutos.';

const getSessionTempExpiresAt = (sessionTemp) => {
  try {
    const { exp } = jwtDecode(sessionTemp);
    return exp ? exp * 1000 : undefined;
  } catch (error) {
    return undefined;
  }
};

const loginSchema = Yup.object().shape({
  username: Yup.string().when('loginType', {
    is: (loginType) => loginType === loginTypes.USERNAME.value,
    then: Yup.string().required('El RUT no puede quedar vacío'),
    otherwise: Yup.string().required('El username no puede quedar vacío'),
  }),
  password: Yup.string().required('La contraseña no puede quedar vacía'),
  loginType: Yup.number()
    .oneOf([loginTypes.RUT.value, loginTypes.USERNAME.value])
    .required(),
});

export default function LoginForm() {
  const { login, loginWithToken } = useAuth();

  const isMountedRef = useIsMountedRef();
  const recaptchaRef = React.useRef();

  const [showPassword, setShowPassword] = useState(false);
  // Desafío MFA del paso 1, solo en memoria (RF-71):
  // { sessionTemp, method, expiresAt } | null
  const [mfaChallenge, setMfaChallenge] = useState(null);
  // eslint-disable-next-line
  const [errorMessage, setErrorMessage] = useState('');
  // eslint-disable-next-line
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const token = searchParams.get('token');
    const signInToken = async () => {
      if (token) {
        try {
          const captchaToken = await recaptchaRef.current.executeAsync();
          await loginWithToken({
            token,
            captchaToken,
          });
        } catch (error) {
          console.error(error);
          const message = createMessageError(error.message);
          if (error.statusCode >= 500) {
            toast.error(
              'Ha ocurrido un error, comuniquese con soporte si el error persiste.',
            );
          } else {
            toast.error(message);
          }

          if (isMountedRef.current) {
            setErrorMessage(error.message);
          }
        }
      }
    };
    signInToken();

    // eslint-disable-next-line
  }, [searchParams]);

  const formik = useFormik({
    initialValues: {
      loginType: loginTypes.USERNAME.value,
      username: '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const token = await recaptchaRef.current.executeAsync();
        const result = await login({
          ...values,
          captchaToken: token,
        });
        // Usuario con MFA: abrir el modal de código sin resetForm (se conservan
        // las credenciales por si cancela). Sin MFA el contexto ya completó el
        // login y Login.js redirige al detectar isAuthenticated.
        if (result?.mfaRequired && isMountedRef.current) {
          setMfaChallenge({
            sessionTemp: result.sessionTemp,
            method: result.mfaMethod,
            expiresAt: getSessionTempExpiresAt(result.sessionTemp),
          });
        }
      } catch (error) {
        console.error(error);
        const message = createMessageError(error.message);
        if (STEP_ONE_MFA_ERRORS.includes(error.mfa_error)) {
          toast.error(error.message);
        } else if (error.statusCode === 429) {
          toast.error(TOO_MANY_REQUESTS_MESSAGE);
        } else if (error.statusCode >= 500) {
          toast.error(
            'Ha ocurrido un error, comuniquese con soporte si el error persiste.',
          );
        } else {
          toast.error(message);
        }
        resetForm();
        if (isMountedRef.current) {
          setErrorMessage(error.message);
        }
      }
    },
  });

  // Cerrar el modal de código y volver a credenciales. El token de reCAPTCHA ya
  // se consumió en el paso 1: se resetea el widget para el próximo intento.
  const returnToCredentials = () => {
    setMfaChallenge(null);
    if (recaptchaRef.current) recaptchaRef.current.reset();
  };

  const handleMfaBack = () => {
    returnToCredentials();
  };

  const handleMfaSessionEnded = (message) => {
    returnToCredentials();
    toast.error(message);
    // Se limpia solo la contraseña; el usuario ingresado se conserva.
    formik.setFieldValue('password', '', false);
    formik.setFieldTouched('password', false, false);
  };

  /*   const handleSelectChange = (event) => {
    setErrorMessage('');
    const {
      target: { value },
    } = event;
    formik.setFieldValue(
      'loginType',
      typeof value === 'string' ? value.split(',') : value,
    );
  }; */

  return (
    <>
      {mfaChallenge && (
        <MfaCodeDialog
          sessionTemp={mfaChallenge.sessionTemp}
          expiresAt={mfaChallenge.expiresAt}
          onBack={handleMfaBack}
          onSessionEnded={handleMfaSessionEnded}
        />
      )}
      {/* El <form> y el <ReCAPTCHA> quedan siempre montados (el login por
          ?token= usa el reCAPTCHA); el paso de código se muestra encima, en
          un modal que vive fuera de este <form>. */}
      <form
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        onSubmit={formik.handleSubmit}
      >
        <Box>
          <Stack spacing={3}>
            {/* {!!errorMessage && <Alert severity="error">{errorMessage}</Alert>} */}

            <FormControl
              sx={{ display: 'flex' }}
              error={
                formik.touched.loginType && Boolean(formik.errors.loginType)
              }
              required
            >
              {/* <InputLabel id="login-type-select">Tipo de Login</InputLabel> */}
              {/* <Select
              size="small"
              labelId="login-type-select"
              value={formik.values.loginType}
              label="Tipo de Login"
              onChange={handleSelectChange}
              error={errorMessage.includes('username') && Boolean(errorMessage)}
            >
              {[ */}
              {/* { value: loginTypes.RUT.value, label: loginTypes.RUT.label }, */}
              {/* {
                  value: loginTypes.USERNAME.value,
                  label: loginTypes.USERNAME.label,
                },
              ].map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select> */}
              {formik.touched.loginType && formik.errors.loginType && (
                <FormHelperText>{formik.errors.loginType}</FormHelperText>
              )}
            </FormControl>

            <TextField
              size="small"
              fullWidth
              id="username"
              label={'Username'}
              name="username"
              autoComplete="username"
              autoFocus
              value={formik.values.username}
              onChange={formik.handleChange}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
            />

            <TextField
              fullWidth
              size="small"
              type={showPassword ? 'text' : 'password'}
              id="password"
              label="Contraseña"
              name="password"
              autoComplete="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      <Iconify
                        icon={
                          showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'
                        }
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 2 }}
          ></Stack>

          <LoadingButton
            fullWidth
            size="medium"
            type="submit"
            variant="contained"
            loading={formik.isSubmitting}
          >
            Ingresar
          </LoadingButton>
        </Box>
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={GOOGLE_CAPTCHA_KEY}
        />
      </form>
    </>
  );
}
